import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import type { ImageAttachment } from "@/components/panels/image-preview-strip";
import {
  commentDictationClaimedBy,
  holdDictationSurface,
  releaseDictationSurface,
} from "@/desktop/comment-dictation-target";
import { registerDictationComposer } from "@/desktop/composer-dictation-focus";
import {
  dictationMicState,
  isDictationSettled as isSettled,
} from "@/desktop/dictation-status";
import { useCommentDictationClaim } from "@/desktop/use-comment-dictation-claim";
import { useDictation } from "@/desktop/use-dictation";
import { isTauri } from "@/lib/is-tauri";
import type { DictationMicControl, DictationSurface } from "./input-bar-types";
import { useLiveDictationDraft } from "./use-live-dictation-draft";
import {
  useVoiceRememberOffer,
  type VoiceRememberOffer,
} from "./use-voice-remember-offer";

/** The mic of one composer: every input bar and the tray composer. The
 *  composer owns a recording its own mic starts, one dictation anywhere
 *  starts while the user types in it (`containerRef`), and one the global
 *  voice shortcut starts while its `surface` is open. While it owns the
 *  recording, speech is previewed live in this draft (the input bar's
 *  live-dictation logic) and the final transcript replaces the preview.
 *  Nothing is sent: the user reviews the draft and sends it. When the user
 *  then corrects a dictated word, the composer offers to remember it. */
export function useComposerDictation({
  id,
  enabled,
  value,
  onChange,
  onDelivered,
  containerRef,
  surface,
}: {
  /** Claim id, one per composer, stable across target switches. null = no mic. */
  id: string | null;
  /** The composer can take input. Gates NEW recordings only. */
  enabled: boolean;
  value: string;
  onChange: (value: string) => void;
  /** Runs one frame after the final transcript lands (return focus to the field). */
  onDelivered?: () => void;
  /** The composer's DOM: focus inside it lets dictation anywhere reach it. */
  containerRef?: RefObject<HTMLElement | null>;
  /** Set while the composer's surface is open (`DictationSurface`). */
  surface?: DictationSurface;
}): {
  mic: DictationMicControl | undefined;
  remember: VoiceRememberOffer | null;
} {
  const status = useDictation((state) => state.status);
  // A surface's claim is made by the shortcut, not this hook: read it from
  // this window's claim whenever the mirrored owner changes.
  const claimedHere = useDictation(
    (state) =>
      id !== null && state.ownerId === id && commentDictationClaimedBy(id),
  );
  const desktop = isTauri();
  const onDeliveredRef = useRef(onDelivered);
  onDeliveredRef.current = onDelivered;
  const previousStatusRef = useRef(status);
  const ownsRecordingRef = useRef(false);
  const [ownsRecording, setOwnsRecordingState] = useState(false);
  const {
    offer: remember,
    setDictated,
    clear: clearRemember,
  } = useVoiceRememberOffer(value);

  // Ownership drives the mic UI and live draft, and drops as soon as the
  // recording settles. The claim itself outlives an error until idle (see
  // the shared hook), so a caller can tell its own errored recording apart.
  const setOwnsRecording = useCallback((owns: boolean) => {
    ownsRecordingRef.current = owns;
    setOwnsRecordingState(owns);
  }, []);

  useEffect(() => {
    const previous = previousStatusRef.current;
    previousStatusRef.current = status;
    if (isSettled(status) && !isSettled(previous)) setOwnsRecording(false);
    if (status === "recording" && previous !== "recording") clearRemember();
  }, [clearRemember, setOwnsRecording, status]);

  // A recording this composer owns keeps its target until it settles, even
  // if the composer becomes blocked meanwhile, so speech never leaks to Denker.
  const armed = desktop && (ownsRecording || claimedHere);
  const { commitFinalTranscript } = useLiveDictationDraft({
    enabled: armed,
    value,
    onChange,
    targetId: id ?? undefined,
  });

  const deliver = (text: string) => {
    setDictated(commitFinalTranscript(text));
    requestAnimationFrame(() => onDeliveredRef.current?.());
  };
  const deliverRef = useRef(deliver);
  deliverRef.current = deliver;

  const { toggle } = useCommentDictationClaim({
    id: id ?? "",
    disableScreenshotCapture: true,
    deliverTranscript: (text) => deliver(text),
  });

  const toggleMic = useCallback(() => {
    if (id === null) return;
    const settled = isSettled(useDictation.getState().status);
    // A recording owned by another composer or by Denker is not ours to stop.
    if (!settled && !ownsRecordingRef.current) return;
    if (settled && !enabled) return;
    if (settled) setOwnsRecording(true);
    const toggled = toggle();
    if (!toggled) return;
    void toggled.finally(() => {
      // Dictation did not start, or already finished: stop owning the UI.
      if (isSettled(useDictation.getState().status)) setOwnsRecording(false);
    });
  }, [enabled, id, setOwnsRecording, toggle]);
  const toggleMicRef = useRef(toggleMic);
  toggleMicRef.current = toggleMic;

  useEffect(() => {
    if (!desktop || id === null || !containerRef) return;
    return registerDictationComposer(id, {
      contains: (node) => containerRef.current?.contains(node) ?? false,
      enabled,
      toggle: () => toggleMicRef.current(),
    });
  }, [containerRef, desktop, enabled, id]);

  // The surface takes the shortcut's recordings while it is open; re-held as
  // its options change, released (cancelling a recording it took) on close.
  useEffect(() => {
    if (!desktop || id === null || !surface) return;
    holdDictationSurface({
      id,
      disableScreenshotCapture: surface.disableScreenshotCapture,
      deliverTranscript: (text: string, images: ImageAttachment[]) => {
        deliverRef.current(text);
        if (images.length) surface.onImages(images);
      },
    });
  }, [desktop, id, surface]);
  const surfaceOpen = Boolean(surface);
  useEffect(() => {
    if (!desktop || id === null || !surfaceOpen) return;
    return () => {
      const owned = commentDictationClaimedBy(id);
      releaseDictationSurface(id);
      const dictation = useDictation.getState();
      if (owned && !isSettled(dictation.status)) void dictation.cancel();
    };
  }, [desktop, id, surfaceOpen]);

  const state = dictationMicState(status, ownsRecording);
  return {
    // A recording this composer owns keeps its stop button, even while the
    // composer is blocked (e.g. a switched-to sub-agent still loading).
    mic:
      desktop && id !== null
        ? {
            ...state,
            disabled: state.disabled || (!enabled && !ownsRecording),
            onClick: toggleMic,
          }
        : undefined,
    remember,
  };
}
